import { ApiError } from 'utils/api';
import { datadogTrackEvent, logError } from 'utils/datadog';
import { GuestFormState } from './GuestFormSessionContext';

/** Map a failed guest-form request to the screen that should render. */
export const stateFromError = (error: unknown): GuestFormState => {
  const code = error instanceof ApiError ? error.code : undefined;
  switch (code) {
    case 'error.form-invitation.already-submitted':
    case 'error.form-response.already-submitted':
      return 'submitted';
    case 'error.form-invitation.expired':
      return 'expired';
    case 'error.form-invitation.revoked':
      return 'revoked';
    case 'error.form.closed':
      return 'closed';
    case 'error.form-version.invalidated':
      return 'replaced';
    case 'error.guest.unavailable':
      return 'unavailable';
    default:
      // error.form-invitation.invalid, error.form-invitation.form-mismatch,
      // error.guest.session-expired, unknown → treat as a link that does not work
      return 'invalid';
  }
};

/**
 * Outcomes that are a normal part of a link's life — reopened after submitting, the invitation
 * window over, the short session lapsed, the operator closed the form. Each renders a friendly
 * screen, so they are counted as RUM actions rather than reported as errors.
 *
 * `error.form-invitation.invalid` and `error.form-version.invalidated` are deliberately absent: a
 * link that should work but does not, and a version withdrawn while someone was answering it, are
 * the two cases that must stay loud in Error Tracking.
 */
const EXPECTED_CODES = new Set([
  'error.form-invitation.already-submitted',
  'error.form-response.already-submitted',
  'error.form-invitation.expired',
  'error.form-invitation.revoked',
  'error.form.closed',
  'error.guest.session-expired'
]);

/**
 * Codes after which no write can ever succeed again. The autosave loop and the submit path must
 * stop on these and swap to the matching screen — retrying would only produce more 400s.
 *
 * `error.guest.session-expired` is NOT terminal: the session lapses while the invitation stays
 * valid, which is exactly what re-redeeming repairs.
 */
const TERMINAL_CODES = new Set([
  'error.form-invitation.already-submitted',
  'error.form-response.already-submitted',
  'error.form-invitation.expired',
  'error.form-invitation.revoked',
  'error.form-invitation.invalid',
  'error.form-invitation.form-mismatch',
  'error.form-version.invalidated',
  'error.form.closed'
]);

export const isTerminalFormError = (error: unknown): error is ApiError =>
  error instanceof ApiError && TERMINAL_CODES.has(error.code);

/** A submit the server refused because an answer is missing or unacceptable. */
const ANSWER_REJECTION_CODES = new Set([
  'error.form-answer.required',
  'error.form-answer.malformed',
  'error.form-answer.option.unknown',
  'error.form-answer.other-text.required',
  'error.form-answer.other-text.not-allowed',
  'error.form-answer.other-text.too-long',
  'error.form-answer.too-large'
]);

/**
 * A rejection the respondent can act on, with the block it concerns. The runner uses this to send
 * someone back to the question that needs fixing instead of showing a dead end — the server is the
 * authority on validity, so its verdict has to be renderable, not just loggable.
 */
export const answerRejectionOf = (
  error: unknown
): { code: string; instanceId?: string } | null => {
  if (!(error instanceof ApiError) || !ANSWER_REJECTION_CODES.has(error.code)) {
    return null;
  }
  const instanceId = error.values?.instanceId;
  return {
    code: error.code,
    instanceId: typeof instanceId === 'string' ? instanceId : undefined
  };
};

export type FormErrorStage =
  | 'redeem'
  | 'recovery'
  | 'autosave'
  | 'submit'
  | 'language-switch';

/**
 * Report a failed request: an expected lifecycle outcome becomes a countable RUM action (product
 * keeps the reopen/expiry signal), anything else stays a RUM error.
 */
export const reportFormOutcome = (
  stage: FormErrorStage,
  error: unknown
): void => {
  const code = error instanceof ApiError ? error.code : undefined;
  if (code && EXPECTED_CODES.has(code)) {
    datadogTrackEvent('guest_form_outcome', { stage, code });
    return;
  }
  logError(`Guest form ${stage} failed`, error);
};
